import { FormEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { FormLayout, Label } from '@/components/Forms';
import CurrencyField from '@/components/Forms/CurrencyField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import useAlerts from '@/hooks/useAlerts';
import { selectTeamTestingProperties } from '@/selectors';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';
import { Dispatch, RootState } from '@/utils/store';

import PasteFileNameField from './PasteFileNameField';
import TestingChannelsSelectionField from './TestingChannelsSelectionField';
import ToggableCardHeading, { Box } from './ToggableCardHeading';

type FormDataT = {
  thumbs_and_titles_max_cpv: string;
  thumbs_youtube_channels?: number[];
  thumbs_campaign_budget: string;
  thumbs_paste_file_name: boolean;
};

const InputsBox = styled.div`
  padding: 24px;
  border-top: solid 1px ${({ theme }) => theme.colors.grayscaleLight};
`;

const YoutubeThumbsProperties = () => {
  const dispatch = useDispatch<Dispatch>();

  const [showContent, setShowContent] = useState(false);

  const { teamId } = useParams();

  const alerts = useAlerts();

  const { testingSettings, testingChannels } = useSelector((state: RootState) =>
    selectTeamTestingProperties(state, teamId!),
  );

  const {
    handleSubmit,
    clearErrors,
    setError,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      thumbs_and_titles_max_cpv:
        testingSettings?.thumbs_and_titles_max_cpv?.toString() ?? '',
      thumbs_campaign_budget:
        testingSettings?.thumbs_campaign_budget?.toString() ?? '',
      thumbs_youtube_channels:
        testingChannels?.filter((c) => c.type === 'thumbs')?.map((c) => c.id) ??
        [],
      thumbs_paste_file_name: testingSettings?.thumbs_paste_file_name ?? false,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const properties = await api.post(
          `/v1/teams/${teamId}/testing-settings`,
          {
            ...data,
            network: 'youtube',
            type: 'thumbs',
          },
        );

        dispatch.teams.updateTeamTestingProperties({
          properties,
          teamId: parseInt(teamId!),
        });

        alerts.success('Youtube Thumbs properties and channels saved');
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Box>
      <form onSubmit={onSubmit}>
        <ToggableCardHeading
          setShowContent={setShowContent}
          count={
            testingChannels
              ?.filter((c) => c.type === 'thumbs')
              ?.map((c) => c.id).length
          }
          showContent={showContent}
          title="Thumbs Properties & channels"
          hasSubmitButton
          isSubmitting={isSubmitting}
          isDirty={isDirty}
        />
        <InputsBox>
          <FormLayout>
            <PasteFileNameField<FormDataT>
              control={control}
              name="thumbs_paste_file_name"
            />
            <div>
              <Label htmlFor="thumbsMaxCpv">Thumbs maximum CPV</Label>
              <CurrencyField<FormDataT>
                control={control}
                name="thumbs_and_titles_max_cpv"
                inputId="thumbsMaxCpv"
                placeholder="Add amount"
              />
              {!!errors.thumbs_and_titles_max_cpv && (
                <ValidationMessage>
                  {errors.thumbs_and_titles_max_cpv.message}
                </ValidationMessage>
              )}
            </div>
            <div>
              <Label htmlFor="thumbsBudget">Thumbs campaign budget</Label>
              <CurrencyField<FormDataT>
                control={control}
                name="thumbs_campaign_budget"
                inputId="thumbsBudget"
                placeholder="Add amount"
              />
              {!!errors.thumbs_campaign_budget && (
                <ValidationMessage>
                  {errors.thumbs_campaign_budget.message}
                </ValidationMessage>
              )}
            </div>
          </FormLayout>
        </InputsBox>
        {!!showContent && (
          <TestingChannelsSelectionField<FormDataT>
            control={control}
            name="thumbs_youtube_channels"
            label="Channels Options"
          />
        )}
      </form>
    </Box>
  );
};

export default YoutubeThumbsProperties;
